import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import WysiwygOutlinedIcon from '@mui/icons-material/WysiwygOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';

import type { PatientIntakeStep } from '@shared/patient-intake-status';

import { ConfirmationModalWrapper } from '../../../../../components/ConfirmationModalWrapper.jsx';
import { usePatientIntakeStatusContext } from '../../hooks';
import { useActionPromptContext } from './ActionPromptContext.tsx';

const getTitle = (step: PatientIntakeStep) => {
  if (step.stepType === 'BASE_STEP') {
    return step.id.replaceAll('_', ' ');
  }

  return step.title;
};

type ActionMenuProps = {
  children: React.ReactElement<{ onClick: (event: React.MouseEvent<HTMLElement>) => void }>;
};

export const ActionMenu = ({ children }: ActionMenuProps) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();
  const {
    onEdit,
    onClick,
    onDelete,
    onShowHistory,
    onToggleDisabled,
    step,
    isDisabled,
    currentStatus,
    isViewOnlyMode,
  } = useActionPromptContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onAction = (action: undefined | (() => void)) => {
    return (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      action?.();
      handleMenuClose();
    };
  };

  if (!isHealthcareProvider) {
    return null;
  }

  const title = getTitle(step);

  return (
    <>
      {React.cloneElement(children, { onClick: handleMenuClick })}

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
        {!isViewOnlyMode && (
          <MenuItem onClick={onAction(onEdit)}>
            <ListItemIcon>
              <UpdateOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Update Status</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={onAction(onClick)} disabled={currentStatus?.status !== 'COMPLETED'}>
          <ListItemIcon>
            <WysiwygOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>

        {!!onDelete && !isViewOnlyMode && (
          <ConfirmationModalWrapper
            title="Delete Step"
            description={`Are you sure you want to delete the step "${title}"? This action cannot be undone.`}
            confirmText="Delete"
            confirmColour="secondary"
            titleIcon={DeleteIcon}
            onConfirm={onDelete}
          >
            <MenuItem>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </ConfirmationModalWrapper>
        )}

        {!!onToggleDisabled && !isViewOnlyMode && (
          <MenuItem onClick={onAction(onToggleDisabled)}>
            <ListItemIcon>
              {isDisabled ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{isDisabled ? 'Enable' : 'Disable'}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={onAction(onShowHistory)}>
          <ListItemIcon>
            <TimelineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View History</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
